// src/providers/authProvider.ts
import { AuthProvider } from 'react-admin';
import keycloak from '@/lib/keycloak';
import { syncKeycloakToLocalStorage } from '@/utils/keycloakSync';
import { clearAuthData, getAssignedGroups, getAssignedHotels, getCurrentRole } from '@/utils/auth';
import type { UserRole } from '@/types/models';

const PORTAL_URL = import.meta.env.VITE_PORTAL_URL || 'https://portal.roommatik.com';
const PORTAL_LOGIN_URL = `${PORTAL_URL}?app=insight`;

/**
 * Event emitter for auth state changes
 * Used to notify components when auth state changes
 */
export const authEvents = new EventTarget();

export const AUTH_EVENTS = {
  LOGIN_SUCCESS: 'auth:login:success',
  LOGOUT: 'auth:logout',
  ROLE_CHANGED: 'auth:role:changed',
} as const;

// Flag to prevent checkAuth from rejecting during logout redirect
let isLoggingOut = false;

/**
 * Authentication provider for React Admin (Keycloak-only).
 *
 * Login is handled externally by keycloak-js (check-sso / portal redirect).
 * This provider is a thin wrapper that keeps React Admin happy and ensures
 * the token stays fresh.
 */
const authProvider: AuthProvider = {
  /**
   * Login — not called because Keycloak handles login externally.
   */
  login: async () => {
    return Promise.resolve();
  },

  /**
   * Logout — end the Keycloak session and redirect to the portal.
   */
  logout: async () => {
    isLoggingOut = true;

    const previousRole = localStorage.getItem('role');

    // Clear localStorage auth data
    clearAuthData();
    localStorage.removeItem('hotelName');
    localStorage.removeItem('groupName');
    localStorage.removeItem('selectedHotelId');
    localStorage.removeItem('selectedHotelData');

    // Emit logout event
    authEvents.dispatchEvent(
      new CustomEvent(AUTH_EVENTS.LOGOUT, { detail: { previousRole } })
    );

    // Keycloak logout — redirects to portal
    keycloak.logout({ redirectUri: PORTAL_URL });

    // Return a never-resolving promise so React Admin doesn't render anything
    // while the browser navigates to the portal
    return new Promise(() => {});
  },

  /**
   * Check authentication status.
   * Ensures the token is still valid; tries silent refresh if needed.
   */
  checkAuth: async () => {
    if (isLoggingOut) {
      return new Promise(() => {});
    }
    if (!keycloak.authenticated) {
      return Promise.reject({ message: 'Not authenticated', redirectTo: false });
    }

    try {
      // Refresh token if it expires within 30 seconds
      const refreshed = await keycloak.updateToken(30);
      if (refreshed) {
        syncKeycloakToLocalStorage(keycloak);
      }
      return Promise.resolve();
    } catch {
      // Token refresh failed — session expired
      clearAuthData();
      window.location.href = PORTAL_LOGIN_URL;
      return Promise.reject({ message: 'Session expired', redirectTo: false });
    }
  },

  /**
   * Handle API error responses (401/403).
   */
  checkError: async (error: any) => {
    const status = error?.status || error?.response?.status;

    if (status === 401 || status === 403) {
      try {
        await keycloak.updateToken(30);
        syncKeycloakToLocalStorage(keycloak);
        // Token refreshed — let the caller retry
        return Promise.resolve();
      } catch {
        // Cannot refresh — force logout
        clearAuthData();
        window.location.href = PORTAL_LOGIN_URL;
        return Promise.reject({ redirectTo: false });
      }
    }

    return Promise.resolve();
  },

  /**
   * Get user permissions (role). Routed through getCurrentRole() (not a raw
   * localStorage read) so React Admin's usePermissions() — which drives menu
   * and resource-level access app-wide — reflects the impersonated role while
   * an admin/superadmin is acting as a lower-role user.
   */
  getPermissions: async () => {
    if (isLoggingOut) {
      return new Promise(() => {});
    }
    const role = getCurrentRole();
    if (!role) {
      return Promise.reject({ message: 'No role found' });
    }
    return Promise.resolve(role as UserRole);
  },

  /**
   * Get user identity for the app bar.
   */
  getIdentity: async () => {
    const parsed = keycloak.tokenParsed as any;
    if (!parsed) {
      return Promise.reject({ message: 'No user identity found' });
    }

    return Promise.resolve({
      id: parsed.sub ?? '',
      fullName:
        parsed.name ||
        [parsed.given_name, parsed.family_name].filter(Boolean).join(' ') ||
        parsed.preferred_username ||
        parsed.email ||
        '',
      avatar: undefined,
      email: parsed.email,
      role: getCurrentRole() || undefined,
      hotelId: localStorage.getItem('hotelId') || undefined,
      groupId: localStorage.getItem('groupId') || undefined,
      assignedGroups: getAssignedGroups(),
      assignedHotels: getAssignedHotels(),
    });
  },

  /**
   * Handle authentication callback — not needed with keycloak-js.
   */
  handleCallback: async () => {
    return Promise.resolve();
  },
};

export default authProvider;
